import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { formatUsdc, formatZenova, bnToNumber } from '../../utils/format';
import { InlineLoader } from '../common/Loader';
import { ZENOVA_DECIMALS, USDC_DECIMALS } from '../../config/contracts';

export const ContractStats = () => {
  const { viewsR, storageR } = useContracts();
  const [stats, setStats] = useState(null);
  const [solv, setSolv] = useState(null);
  const [price, setPrice] = useState(null);
  const [usdcMode, setUsdcMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !storageR) return;
      try {
        const [s, sv, p, m] = await Promise.all([
          viewsR.getContractStats(),
          viewsR.getUsdcSolvency().catch(() => null),
          storageR.zenovaPricePerUsdc().catch(() => null),
          storageR.usdcClaimMode().catch(() => false)
        ]);
        if (cancelled) return;
        setStats(s);
        setSolv(sv);
        setPrice(p);
        setUsdcMode(m);
      } catch (e) {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [viewsR, storageR]);

  if (!stats) return <InlineLoader text="Loading stats..." />;

  // Compute ZENOVA needed to cover the USDC-denominated obligations at current price.
  // (Under normal mode ALL payouts leave as ZENOVA — user net + admin fee.)
  let zenovaNeeded = null;
  let zenovaSolvent = false;
  let zenovaShortfall = null;
  if (solv && price && !price.isZero()) {
    // zenova = obligated_usdc(6dec) * 1e18 / price(6dec)
    zenovaNeeded = solv.totalUsdcObligated.mul(ethers.BigNumber.from(10).pow(ZENOVA_DECIMALS)).div(price);
    zenovaSolvent = stats.zenovaBalance.gte(zenovaNeeded);
    if (!zenovaSolvent) zenovaShortfall = zenovaNeeded.sub(stats.zenovaBalance);
  }

  return (
    <div>
      <div className="admin-form-grid mb-24">
        <div className="admin-metric">
          <div className="metric-label">Total Users</div>
          <div className="metric-value">{bnToNumber(stats.totalUsers)}</div>
        </div>
        <div className="admin-metric">
          <div className="metric-label">Active Users</div>
          <div className="metric-value">{bnToNumber(stats.activeUsers)}</div>
        </div>
        <div className="admin-metric">
          <div className="metric-label">Total Invested</div>
          <div className="metric-value">${formatUsdc(stats.totalInvestment)}</div>
        </div>
        <div className="admin-metric">
          <div className="metric-label">Contract USDC</div>
          <div className="metric-value">${formatUsdc(stats.usdcBalance)}</div>
        </div>
        <div className="admin-metric">
          <div className="metric-label">Contract ZENOVA</div>
          <div className="metric-value">{formatZenova(stats.zenovaBalance)}</div>
        </div>
        <div className="admin-metric">
          <div className="metric-label">Zenova Price</div>
          <div className="metric-value">${formatUsdc(price, 6)}</div>
        </div>
      </div>

      {/* ============ ZENOVA RESERVE HEALTH — the one that matters ============ */}
      {solv && price && !price.isZero() && (
        <div className={`contract-status-card ${zenovaSolvent ? 'linked' : 'error'}`}>
          <div className="status-icon-lg">{zenovaSolvent ? '✓' : '⚠️'}</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ marginBottom: 6 }}>
              ZENOVA Reserve — {zenovaSolvent ? 'Healthy' : 'INSUFFICIENT'}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
              User claims and admin fees are paid in ZENOVA. This is the reserve users draw from.
            </p>
            <div className="kv-list" style={{ fontSize: 13 }}>
              <div className="kv-row">
                <span className="kv-key">ZENOVA in Contract</span>
                <span className="kv-val" style={{ color: 'var(--neon-cyan)' }}>{formatZenova(stats.zenovaBalance)}</span>
              </div>
              <div className="kv-row">
                <span className="kv-key">USDC-denominated Obligations</span>
                <span className="kv-val">${formatUsdc(solv.totalUsdcObligated)}</span>
              </div>
              <div className="kv-row">
                <span className="kv-key">ZENOVA Needed (at current price)</span>
                <span className="kv-val" style={{ color: zenovaSolvent ? 'var(--neon-green)' : 'var(--neon-red)' }}>
                  {formatZenova(zenovaNeeded)}
                </span>
              </div>
              {!zenovaSolvent && zenovaShortfall && (
                <div className="kv-row">
                  <span className="kv-key" style={{ color: 'var(--neon-red)' }}>Shortfall</span>
                  <span className="kv-val" style={{ color: 'var(--neon-red)', fontWeight: 800 }}>
                    {formatZenova(zenovaShortfall)}
                  </span>
                </div>
              )}
            </div>
            {!zenovaSolvent && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--neon-red)' }}>
                Deposit more ZENOVA into Storage (via "Reserves" → Deposit) so users can claim.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ USDC info — only matters in emergency mode ============ */}
      {solv && (
        <div className={`contract-status-card ${usdcMode ? (solv.solvent ? 'linked' : 'error') : 'unlinked'}`} style={{ marginTop: 16 }}>
          <div className="status-icon-lg">{usdcMode ? '⚠️' : 'ℹ️'}</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ marginBottom: 6 }}>
              USDC Reserve {usdcMode ? '(EMERGENCY MODE ACTIVE)' : '(Informational)'}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
              {usdcMode
                ? 'Emergency USDC-claim mode is ON — users receive USDC instead of ZENOVA. Reserve must cover all obligations.'
                : 'USDC only leaves the contract if emergency USDC-claim mode is enabled. Under normal operation this reserve is not spent.'}
            </p>
            <div className="kv-list" style={{ fontSize: 13 }}>
              <div className="kv-row">
                <span className="kv-key">USDC Balance</span>
                <span className="kv-val">${formatUsdc(solv.usdcBalance)}</span>
              </div>
              <div className="kv-row">
                <span className="kv-key">Total Obligated (USDC)</span>
                <span className="kv-val">${formatUsdc(solv.totalUsdcObligated)}</span>
              </div>
              {usdcMode && !solv.solvent && (
                <div className="kv-row">
                  <span className="kv-key" style={{ color: 'var(--neon-red)' }}>Shortfall</span>
                  <span className="kv-val" style={{ color: 'var(--neon-red)', fontWeight: 800 }}>
                    ${formatUsdc(solv.totalUsdcObligated.sub(solv.usdcBalance))}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
